import { useEffect as useDrawerBaselineEffect } from "react";
import { useFinanceOperation } from "../../../app/operations/useFinanceOperation";
import { useFormDirty } from "../../../../../../../shared/frontend/forms/useFormDirty";
import { cssSpacing, spacing } from "../../../app/design-system";
import { DeleteOutlined } from "@ant-design/icons";
import { DatePicker } from "../../../../../../../shared/frontend/components/ControlledPickers";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import { Button, Descriptions, Divider, Form, Input, InputNumber, Select, Typography } from "../../../../../../../shared/frontend/ui";
import type { ModalStaticFunctions } from "../../../../../../../shared/frontend/ui";
import type { DefaultOptionType } from "../../../../../../../shared/frontend/ui";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateOnly,formatMoney } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { paymentMethodLabel } from "../../../app/utils/paymentMethod";
import { FormDrawer } from "../../../components/FormDrawer";
import type { PaymentRow,PersonPaymentFormValues } from "./personDetailTypes";
import { formatDateOnlyIso } from "./personDetailUtils";
import type { Feedback } from "../../../../../../../shared/frontend/feedback/useFeedback";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

const DRAWER_WIDTH = 500;

type ApiClient = {
  del: (url: string) => Promise<unknown>;
  put: (url: string, body?: unknown) => Promise<unknown>;
};

type Props = {
  open: boolean;
  payment: PaymentRow | null;
  form: FormInstance<PersonPaymentFormValues>;
  api: ApiClient;
  message: Feedback;
  modal: Pick<ModalStaticFunctions, "confirm">;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  personId: string;
  decimalSeparator: string;
  currencyCode: string;
  moneyPlaceholder: string;
  dateInputFormat: string | string[];
  paymentMethodNameByType: Record<number, string>;
  paymentMethodSelectOptions: DefaultOptionType[];
  onClose: () => void;
  /** Zahlung nur ansehen (nicht änderbar oder ohne Finanzrecht). */
  readOnly?: boolean;
  /** Stornieren ist eine eigene Sonderaktion („Zahlungen stornieren“). */
  canCancel?: boolean;
};

export function PersonPaymentEditDrawer({
  open,
  payment,
  form,
  api,
  message,
  modal,
  text,
  units,
  personId,
  decimalSeparator,
  currencyCode,
  moneyPlaceholder,
  dateInputFormat,
  paymentMethodNameByType,
  paymentMethodSelectOptions,
  onClose,
  readOnly = false,
  canCancel = false
}: Props) {
  const operation = useFinanceOperation(personId);
  const { dirty: drawerDirty, resetDirty: resetDrawerDirty } = useFormDirty(form);
  const drawerOpen = open;
  useDrawerBaselineEffect(() => { if (drawerOpen) resetDrawerDirty(); }, [drawerOpen, resetDrawerDirty]);

  const handleCancelPayment = async () => {
    if (!payment) return;

    const ok = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: text("Cancel payment"),
        okText: text("Void"),
        okButtonProps: { danger: true },
        cancelText: text("Cancel"),
        width: DRAWER_WIDTH,
        content: (
          <div>
            <Typography.Text>
              {text("Do you really want to cancel this payment?")}
            </Typography.Text>
            <Divider style={{ margin: cssSpacing(spacing.md, 0) }} />
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={text("Amount")}>
                {formatMoney(Number(payment.amount || 0), units)}
              </Descriptions.Item>
              <Descriptions.Item label={text("Payment date")}>
                {formatDateOnly(String(payment.paidDate || "").slice(0, ISO_DATE_LENGTH), units) || "—"}
              </Descriptions.Item>
              {payment.method && (
                <Descriptions.Item label={text("Payment method")}>
                  {paymentMethodLabel(payment.method, paymentMethodNameByType) || payment.method}
                </Descriptions.Item>
              )}
              {payment.reference && (
                <Descriptions.Item label={text("Reference")}>
                  {payment.reference}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        ),
        onOk: () => resolve(true),
        onCancel: () => resolve(false)
      });
    });
    if (!ok) return;

    try {
      await operation.execute(async (committed) => {
        await api.del(`/api/v1/persons/${encodeURIComponent(personId)}/payments/${encodeURIComponent(payment.id)}`);
        committed();
        message.success(text("Payment cancelled."));
        onClose();
      });
    } catch {
      // Central API toast shows error
    }
  };

  const handleFinish = async (values: PersonPaymentFormValues) => {
    if (!payment) return;
    if (!values.amount || values.amount <= 0) {
      message.warning(text("Please enter an amount."));
      return;
    }

    try {
      await operation.execute(async (committed) => {
        const paidDateIso = values.paidDate ? formatDateOnlyIso(values.paidDate) : null;
        await api.put(`/api/v1/persons/${encodeURIComponent(personId)}/payments/${encodeURIComponent(payment.id)}`, {
          amount: Number(values.amount || 0),
          paidDate: paidDateIso || undefined,
          method: values.method || null,
          reference: values.reference || null
        });
        message.success(text("Payment saved."));
        committed();
        onClose();
      });
    } catch {
      // Central API toast shows error
    }
  };

  return (
    <FormDrawer mode="edit" dirty={drawerDirty}
      title={readOnly ? "Payment" : "Edit payment"}
      open={open}
      onCancel={onClose}
      readOnly={readOnly}
      formId="payment-edit-form"
      submitting={operation.pending}
      extra={canCancel ? (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={handleCancelPayment}
        >
          {text("Void")}
        </Button>
      ) : undefined}
    >
      <Form
        id="payment-edit-form"
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={handleFinish}
      >
        <Form.Item
          name="amount"
          label={text("Amount")}
          rules={[{ required: true, message: text("Amount is required") }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0.01}
            step={0.01}
            decimalSeparator={decimalSeparator}
            addonAfter={currencyCode}
            placeholder={moneyPlaceholder}
          />
        </Form.Item>
        <Form.Item
          name="paidDate"
          label={text("Payment date")}
          rules={[{ required: true, message: text("Payment date is required") }]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format={dateInputFormat}
            allowClear={false}
          />
        </Form.Item>
        <Form.Item
          name="method"
          label={text("Payment method")}
          rules={[{ required: true, message: text("Payment method is required") }]}
        >
          <Select
            options={paymentMethodSelectOptions}
            placeholder={text("Select payment method…")}
          />
        </Form.Item>
        <Form.Item
          name="reference"
          label={text("Reference")}
        >
          <Input placeholder={text("Optional")} />
        </Form.Item>
      </Form>
    </FormDrawer>
  );
}
